import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserRequests,
  getTherapistRequests,
} from "../../redux/requestSlice";
import { clearUnread, setActiveRoom, removeActiveRoom } from "../../redux/chatSlice";
import ConversationList from "./ConversationList";
import ChatWindow from "./ChatWindow";
import UserPage from "../../common/UserPage/UserPage";
import Footer from "../../common/Footer/Footer";

const getOtherPartyName = (req, role) => {
  if (role === "therapist") {
    const u = req.userId;
    if (!u) return "Client";
    if (typeof u === "object")
      return `${u.name || ""} ${u.surname || ""}`.trim() || u.email || "Client";
  } else {
    const t = req.therapistId;
    if (!t) return "Therapist";
    if (typeof t === "object")
      return (
        `${t.name || ""} ${t.surname || ""}`.trim() || t.email || "Therapist"
      );
  }
  return "-";
};

const ACTIVE_STATUSES = new Set(["accepted", "confirmed", "active", "ongoing"]);

const getOtherPartyId = (req, role) => {
  if (role === "therapist") {
    const u = req.userId;
    return typeof u === "object" ? String(u._id || u.id || "") : String(u || "");
  }
  const t = req.therapistId;
  return typeof t === "object" ? String(t._id || t.id || "") : String(t || "");
};

const ChatPage = ({ role, SidebarComponent }) => {
  const dispatch = useDispatch();
  const { requests: fetchedRequests = [], loading } = useSelector(
    (state) => state.requests,
  );
  const therapistRequests = useSelector(
    (state) => state.therapist?.therapist?.requests,
  );
  const userRequests = useSelector(
    (state) => state.users?.currentUser?.requests,
  );
  const picture = useSelector((state) =>
    role === "therapist"
      ? state.therapist?.therapist?.picture
      : state.users?.currentUser?.picture,
  );
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedRelatedIds, setSelectedRelatedIds] = useState([]);
  const [mobileView, setMobileView] = useState("list");
  const selectedIdRef = useRef(null);

  const requests = (() => {
    const fallback = role === "therapist" ? therapistRequests : userRequests;
    const merged = Array.isArray(fetchedRequests) ? [...fetchedRequests] : [];
    (fallback || []).forEach((r) => {
      const id = r._id || r.id;
      if (id && !merged.find((m) => (m._id || m.id) === id)) merged.push(r);
    });
    return merged;
  })();

  useEffect(() => {
    if (role === "therapist") {
      dispatch(getTherapistRequests());
    } else {
      dispatch(getUserRequests());
    }
  }, [dispatch, role]);

  const selectedId = selectedRequest?._id || selectedRequest?.id;
  const otherPartyName = selectedRequest
    ? getOtherPartyName(selectedRequest, role)
    : "";

  // Keep ref in sync so the unmount cleanup can remove the last active room
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  // Remove active room when user leaves the chat page
  useEffect(() => {
    return () => {
      if (selectedIdRef.current) {
        dispatch(removeActiveRoom({ roomId: selectedIdRef.current }));
      }
    };
  }, [dispatch]);

  const handleSelect = (req) => {
    const id = req._id || req.id;
    // Aynı kişiyle ilgili tüm aktif request ID'lerini bul
    const partyId = getOtherPartyId(req, role);
    const allRelated = requests
      .filter((r) => ACTIVE_STATUSES.has(r.status?.toLowerCase()) && getOtherPartyId(r, role) === partyId)
      .map((r) => String(r._id || r.id))
      .filter(Boolean);

    if (id) {
      allRelated.forEach((rid) => dispatch(clearUnread({ requestId: rid })));
      dispatch(setActiveRoom({ roomId: id }));
    }
    setSelectedRequest(req);
    setSelectedRelatedIds(allRelated);
    setMobileView("chat");
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <UserPage customImage={picture} />

      <div className="flex flex-col md:flex-row flex-1 w-full p-0 md:p-4 mt-[-20px] gap-0 md:gap-4 items-start">
        {/* Sidebar */}
        <div className="flex-shrink-0 w-full md:w-auto">
          <SidebarComponent />
        </div>

        {/* Chat container */}
        <div className="w-full md:flex-1 md:min-w-0 mt-2 md:mt-6">
          <div
            className="bg-white rounded-2xl shadow-sm border border-gray-100 flex overflow-hidden h-[calc(100vh-420px)] min-h-[320px] md:h-[calc(100vh-200px)] md:min-h-[480px]"
          >
            {/* Conversation list */}
            <div
              className={`${
                mobileView === "chat" ? "hidden" : "flex"
              } md:flex flex-col w-full md:w-72 shrink-0 border-r border-gray-100`}
            >
              {/* List header */}
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="text-[15px] font-semibold text-gray-900 tracking-tight">
                  Messages
                </h2>
              </div>
              {loading ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#7a4f6a] border-t-transparent" />
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto">
                  <ConversationList
                    requests={requests}
                    selectedId={selectedId}
                    onSelect={handleSelect}
                    role={role}
                  />
                </div>
              )}
            </div>

            {/* Chat window */}
            <div
              className={`${
                mobileView === "list" && !selectedRequest
                  ? "hidden md:flex"
                  : "flex"
              } flex-1 flex-col min-w-0`}
            >
              <ChatWindow
                request={selectedRequest}
                relatedRequestIds={selectedRelatedIds}
                otherPartyName={otherPartyName}
                onBack={() => setMobileView("list")}
              />
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ChatPage;
